/**
 * Utility functions for managing UTM and custom query parameters
 * Captures parameters from URL and persists them across navigation
 */

const UTM_STORAGE_KEY = 'utm_params'

// Standard UTM parameters
const UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_segment',
    'utm_campaign_id',
    'utm_adset_id',
    'utm_ad_id',
    'utm_keyword',
    'utm_matchtype'
] as const

// Custom query parameters specific to the application
const CUSTOM_PARAMS = [
    'Programa',
    'CodPrograma',
    'CEDetalleMedio',
    'CEDetalleFuente',
    'modalidad',
    'codcampana',
    'fbclid',
    'doctipo',
    'form_tuniv'
] as const

// All trackable parameters
const ALL_PARAMS = [...UTM_PARAMS, ...CUSTOM_PARAMS] as const

export type UTMParams = {
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    utm_term?: string
    utm_content?: string
    utm_segment?: string
    utm_campaign_id?: string
    utm_adset_id?: string
    utm_ad_id?: string
    utm_keyword?: string
    utm_matchtype?: string
    Programa?: string
    CodPrograma?: string
    CEDetalleMedio?: string
    CEDetalleFuente?: string
    modalidad?: string
    codcampana?: string
    fbclid?: string
    doctipo?: string
    form_tuniv?: string
}

// Parámetros que NUNCA deben persistirse ni propagarse (sensibles o temporales)
const SENSITIVE_PARAMS = ['token', 'confirmPassword', 'password', 'tokenAccess']

/**
 * Extracts UTM and custom parameters from URL search params
 */
export const extractUTMParams = (searchParams: URLSearchParams): UTMParams => {
    const utmParams: any = {}

    searchParams.forEach((value, key) => {
        // Ignorar explícitamente parámetros sensibles o que no son de marketing
        if (SENSITIVE_PARAMS.includes(key)) return

        // Capturamos si empieza por utm_ o si está en nuestra lista de parámetros conocidos
        if (
            key.toLowerCase().startsWith('utm_') ||
            (ALL_PARAMS as readonly string[]).includes(key)
        ) {
            utmParams[key] = value
        }
    })

    return utmParams
}



/**
 * Saves UTM parameters to localStorage
 */
export const saveUTMParams = (params: UTMParams): void => {
    if (Object.keys(params).length > 0) {
        localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params))
    }
}

/**
 * Retrieves UTM parameters from localStorage
 */
export const getStoredUTMParams = (): UTMParams => {
    try {
        const stored = localStorage.getItem(UTM_STORAGE_KEY)
        return stored ? JSON.parse(stored) : {}
    } catch (error) {
        console.error('Error retrieving UTM params:', error)
        return {}
    }
}

/**
 * Converts UTM params object to URL search string
 */
export const utmParamsToString = (params: UTMParams): string => {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value))
        }
    })

    const result = searchParams.toString()
    return result ? `?${result}` : ''
}

/**
 * Appends UTM parameters to a given URL
 */
export const appendUTMParams = (url: string, params: UTMParams): string => {
    if (Object.keys(params).length === 0) {
        return url
    }

    const [baseUrl, existingParams] = url.split('?')
    const searchParams = new URLSearchParams(existingParams)

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && !searchParams.has(key)) {
            searchParams.append(key, String(value))
        }
    })

    const queryString = searchParams.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

/**
 * Clears stored UTM parameters
 */
export const clearUTMParams = (): void => {
    localStorage.removeItem(UTM_STORAGE_KEY)
}
